import { useState } from "react";
import { toast } from "sonner";
import { Building2, Copy, Link2, Plus, ShieldCheck } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY = { contact_name: "", company_name: "", email: "" };

export default function LeadProviderManager({ providers = [], onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [invite, setInvite] = useState(null);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/intake/vendors/invite", { ...form, aliases: [] });
      setOpen(false);
      setForm(EMPTY);
      setInvite(data);
      await onCreated?.();
      toast.success("Lead provider access created");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create lead provider access");
    } finally {
      setSaving(false);
    }
  };

  const copyInvite = async () => {
    const raw = invite?.invite_link || "";
    if (!raw) return;
    const link = raw.startsWith("http") ? raw : new URL(raw, window.location.origin).toString();
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Provider invitation link copied");
    } catch (_) {
      toast.error("Could not copy automatically. Select and copy the link manually.");
    }
  };

  return (
    <section className="mt-8" data-testid="lead-provider-manager">
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-[#315A7D]" />
            <h2 className="font-heading text-lg font-bold">Lead Providers</h2>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">Give each lead source a restricted portal to submit leads and track only the business they supplied.</p>
        </div>
        <Button type="button" onClick={() => setOpen(true)} data-testid="add-lead-provider">
          <Plus className="mr-1.5 h-4 w-4" /> Add Lead Provider
        </Button>
      </div>

      {providers.length === 0 ? (
        <div className="rounded-lg border border-dashed bg-card px-5 py-8 text-center text-sm text-muted-foreground">
          No lead providers have portal access yet.
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {providers.map((p) => (
            <div key={p.id || p.email} className="rounded-lg border bg-card p-4" data-testid={`lead-provider-${p.id || p.email}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-heading font-semibold">{p.name}</div>
                  <div className="mt-0.5 text-xs text-muted-foreground">{p.title || "Lead Provider"}</div>
                </div>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ring-inset ${p.active === false ? "bg-slate-100 text-slate-600 ring-slate-200" : "bg-emerald-50 text-emerald-700 ring-emerald-200"}`}>
                  {p.active === false ? "Inactive" : "Portal Active"}
                </span>
              </div>
              <div className="mt-3 text-xs text-muted-foreground">{p.email}</div>
              <div className="mt-3 flex items-start gap-2 rounded-md bg-secondary/50 p-2.5 text-[11px] text-muted-foreground">
                <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
                <span>Restricted to their own lead submissions and sanitized status updates. No agency book, credentials, commissions, or internal notes.</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="lead-provider-dialog">
          <DialogHeader>
            <DialogTitle>Add Lead Provider</DialogTitle>
            <DialogDescription>Create the provider relationship and a restricted portal login. They receive a one-time secure activation link and choose their own password.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Contact Name</Label>
                <Input value={form.contact_name} onChange={(e) => setForm((f) => ({ ...f, contact_name: e.target.value }))} required data-testid="provider-contact" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Company / Lead Source</Label>
                <Input value={form.company_name} onChange={(e) => setForm((f) => ({ ...f, company_name: e.target.value }))} required data-testid="provider-company" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} required data-testid="provider-email" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="provider-save">{saving ? "Creating…" : "Create & Invite"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!invite} onOpenChange={(o) => !o && setInvite(null)}>
        <DialogContent className="max-w-lg" data-testid="provider-invite-ready">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Link2 className="h-5 w-5" /> Lead Provider Invitation Ready</DialogTitle>
            <DialogDescription>
              {invite?.vendor?.contact_name || "The provider"} now has restricted portal access for {invite?.vendor?.name || "this lead source"}. The activation link is one-time and expires automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Secure activation link</Label>
            <div className="flex gap-2">
              <Input readOnly value={invite?.invite_link || ""} className="font-mono text-xs" />
              <Button type="button" variant="outline" onClick={copyInvite} disabled={!invite?.invite_link}>
                <Copy className="mr-1.5 h-4 w-4" /> Copy
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Email delivery status: <strong>{invite?.email_status?.status || invite?.email_status || "invitation created"}</strong>
            </p>
          </div>
          <DialogFooter><Button onClick={() => setInvite(null)}>Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
